import React, { useState, useEffect, useRef, useCallback } from "react";
import { Helmet } from "react-helmet";
import {
  FaSignal,
  FaPlay,
  FaStop,
  FaTrash,
  FaInfoCircle,
  FaMapMarkerAlt,
  FaSyncAlt,
  FaCheckCircle,
  FaServer,
} from "react-icons/fa";
import "../css/ping-test.css";

// Servers to ping — ClubHosty infrastructure
const PING_SERVERS = [
  {
    id: "cloud-in",
    name: "Cloud Hosting IN",
    location: "India",
    host: "64.227.169.26",
    flag: "🇮🇳",
  },
  {
    id: "cloud-sg",
    name: "Cloud Hosting SG",
    location: "Singapore",
    host: "143.198.192.121",
    flag: "🇸🇬",
  },
  {
    id: "cloud-us",
    name: "Cloud Hosting US",
    location: "United States",
    host: "134.199.215.89",
    flag: "🇺🇸",
  },
  {
    id: "cpanel-eu",
    name: "cPanel Hosting EU",
    location: "Europe",
    host: "65.108.232.153",
    flag: "🇪🇺",
  },
  {
    id: "shared-eu",
    name: "Shared Hosting EU",
    location: "Europe",
    host: "198.251.89.229",
    flag: "🇪🇺",
  },
  {
    id: "shared-us",
    name: "Shared Hosting US",
    location: "United States",
    host: "173.211.19.52",
    flag: "🇺🇸",
  },
];

const MAX_HISTORY = 20;
const PING_INTERVAL_MS = 2000;

function getQuality(ms) {
  if (ms === null) return "idle";
  if (ms < 80) return "good";
  if (ms < 200) return "fair";
  return "poor";
}

function getStatusLabel(ms, pinging) {
  if (pinging) return "Pinging...";
  if (ms === null) return "Idle";
  if (ms < 80) return "Excellent";
  if (ms < 200) return "Fair";
  return "Poor";
}

function getStatusClass(ms, pinging) {
  if (pinging) return "status-pinging";
  if (ms === null) return "status-idle";
  if (ms < 80) return "status-good";
  if (ms < 200) return "status-fair";
  return "status-poor";
}

// True when the page is served over HTTPS (production).
// In that case, http:// fetches are blocked as mixed content, so we
// route through the server-side proxy instead.
const IS_HTTPS = typeof window !== 'undefined' && window.location.protocol === 'https:';

// Call the PHP proxy which pings all servers via cURL from the server side.
// Returns { "<ip>": <ms> | null, ... }
async function fetchAllPingsViaProxy() {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 12000);
  try {
    const resp = await fetch('/api/ping-proxy.php', {
      cache: 'no-store',
      signal: ctrl.signal,
    });
    return await resp.json();
  } catch {
    return {};
  } finally {
    clearTimeout(timer);
  }
}

// Fetch-based ping on a warm keep-alive connection (HTTP / localhost only).
// The warmup pass (run once before measurements start) establishes TCP
// keep-alive connections to all servers; subsequent fetches reuse them,
// measuring only the HTTP request/response time ≈ 1 RTT ≈ ICMP ping.
async function measurePing(host) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 2000);
  const start = performance.now();
  try {
    await fetch(`http://${host}/`, {
      method: "HEAD",
      mode: "no-cors",
      cache: "no-store",
      signal: controller.signal,
    });
    return Math.round(performance.now() - start);
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

const ServerCard = ({ server, pingData, isPinging }) => {
  const { latestMs, history, min, max, avg, count, errors } = pingData;
  const quality = getQuality(latestMs);
  const statusClass = getStatusClass(latestMs, isPinging);
  const statusLabel = getStatusLabel(latestMs, isPinging);

  // Normalize bar heights (max bar = 40px)
  const maxVal = Math.max(...history.filter(Boolean), 1);
  const barHeights = history.map((v) =>
    v === null ? null : Math.max(4, Math.round((v / maxVal) * 40))
  );

  return (
    <div className={`server-card ${isPinging ? "active" : ""}`}>
      <div className="server-card-header">
        <div className="server-info">
          <span className="server-flag">{server.flag}</span>
          <div className="server-details">
            <h3>{server.name}</h3>
            <span>{server.location}</span>
          </div>
        </div>
        <span className={`server-status-badge ${statusClass}`}>
          {isPinging ? (
            <FaSyncAlt className="spin" style={{ fontSize: "0.7rem" }} />
          ) : latestMs !== null ? (
            <FaCheckCircle style={{ fontSize: "0.7rem" }} />
          ) : (
            <FaServer style={{ fontSize: "0.7rem" }} />
          )}
          {statusLabel}
        </span>
      </div>

      <div className="ping-meter">
        <div className="ping-value-display">
          <span className={`ping-ms ${quality}`}>
            {latestMs !== null ? latestMs : "—"}
          </span>
          <span className="ping-unit">ms</span>
        </div>

        <div className="ping-bar-chart">
          {Array.from({ length: MAX_HISTORY }).map((_, i) => {
            const h = barHeights[i];
            const v = history[i];
            const q = getQuality(v);
            return (
              <div
                key={i}
                className={`ping-bar ${h === null ? "empty" : q}`}
                style={{ height: h !== null ? `${h}px` : "4px" }}
              />
            );
          })}
        </div>
      </div>

      <div className="ping-stats-row">
        <div className="stat-item">
          <span className="stat-label">Min</span>
          <span className={`stat-value ${getQuality(min)}`}>
            {min !== null ? `${min}ms` : "—"}
          </span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Avg</span>
          <span className={`stat-value ${getQuality(avg)}`}>
            {avg !== null ? `${avg}ms` : "—"}
          </span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Max</span>
          <span className={`stat-value ${getQuality(max)}`}>
            {max !== null ? `${max}ms` : "—"}
          </span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Loss</span>
          <span className={`stat-value ${errors > 0 ? "poor" : count > 0 ? "good" : ""}`}>
            {count > 0
              ? `${Math.round((errors / count) * 100)}%`
              : "—"}
          </span>
        </div>
      </div>
    </div>
  );
};

const PingTest = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [isWarmingUp, setIsWarmingUp] = useState(false);
  const [pingCount, setPingCount] = useState(0);
  const [selectedCount, setSelectedCount] = useState(20);
  const [userLocation, setUserLocation] = useState(null);
  const [log, setLog] = useState([]);
  const [serverData, setServerData] = useState(() =>
    Object.fromEntries(
      PING_SERVERS.map((s) => [
        s.id,
        { latestMs: null, history: Array(MAX_HISTORY).fill(null), min: null, max: null, avg: null, count: 0, errors: 0, total: 0 },
      ])
    )
  );

  const intervalRef = useRef(null);
  const pingCountRef = useRef(0);
  const logContainerRef = useRef(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    // Try to get user location label via IP
    const locationCtrl = new AbortController();
    const locationTimer = setTimeout(() => locationCtrl.abort(), 5000);
    try {
      fetch("https://ipapi.co/json/", { signal: locationCtrl.signal })
        .then((r) => r.json())
        .then((d) => {
          if (isMounted.current && d && d.city) {
            setUserLocation(`${d.city}, ${d.country_name}`);
          }
        })
        .catch(() => {})
        .finally(() => clearTimeout(locationTimer));
    } catch (_) {
      clearTimeout(locationTimer);
    }
    return () => {
      isMounted.current = false;
      clearInterval(intervalRef.current);
    };
  }, []);

  // Auto-scroll log
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [log]);

  const runOnePingRound = useCallback(async () => {
    pingCountRef.current += 1;
    const currentRound = pingCountRef.current;

    const now = new Date();
    const timeStr = now.toLocaleTimeString("en-US", { hour12: false });

    // On HTTPS use the server-side proxy (browser blocks http:// mixed-content).
    // On HTTP (localhost/dev) ping directly from the browser.
    let results;
    if (IS_HTTPS) {
      const proxyData = await fetchAllPingsViaProxy();
      results = PING_SERVERS.map((server) => ({
        server,
        ms: proxyData[server.host] !== undefined ? proxyData[server.host] : null,
      }));
    } else {
      results = await Promise.all(
        PING_SERVERS.map(async (server) => {
          const ms = await measurePing(server.host);
          return { server, ms };
        })
      );
    }

    if (!isMounted.current) return;

    const newLogEntries = results.map(({ server, ms }) => ({
      time: timeStr,
      target: server.name,
      ms,
      quality: getQuality(ms),
    }));

    setLog((prev) => [...prev.slice(-99), ...newLogEntries]);

    setServerData((prev) => {
      const next = { ...prev };
      results.forEach(({ server, ms }) => {
        const d = { ...next[server.id] };
        d.count += 1;
        if (ms === null) {
          d.errors += 1;
        } else {
          d.total = (d.total || 0) + ms;
          d.latestMs = ms;
          d.min = d.min === null ? ms : Math.min(d.min, ms);
          d.max = d.max === null ? ms : Math.max(d.max, ms);
          const successCount = d.count - d.errors;
          d.avg = successCount > 0 ? Math.round(d.total / successCount) : null;
        }
        const newHistory = [...d.history.slice(1), ms];
        d.history = newHistory;
        next[server.id] = d;
      });
      return next;
    });

    setPingCount(currentRound);

    // Stop if reached selected count
    if (currentRound >= selectedCount) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      if (isMounted.current) setIsRunning(false);
    }
  }, [selectedCount]);

  const startPing = useCallback(async () => {
    pingCountRef.current = 0;
    setPingCount(0);
    setIsWarmingUp(true);
    setIsRunning(true);

    // Warmup: only needed for direct browser pings (HTTP/localhost).
    // On HTTPS the proxy handles connections server-side — skip warmup.
    if (!IS_HTTPS) {
      await Promise.all(
        PING_SERVERS.map(({ host }) => {
          const ctrl = new AbortController();
          const t = setTimeout(() => ctrl.abort(), 4000);
          try {
            return fetch(`http://${host}/`, {
              method: "HEAD",
              mode: "no-cors",
              signal: ctrl.signal,
            })
              .catch(() => {})
              .finally(() => clearTimeout(t));
          } catch (_) {
            clearTimeout(t);
            return Promise.resolve();
          }
        })
      );
    }

    if (!isMounted.current) return;
    setIsWarmingUp(false);
    runOnePingRound();
    intervalRef.current = setInterval(runOnePingRound, PING_INTERVAL_MS);
  }, [runOnePingRound]);

  const stopPing = useCallback(() => {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
    setIsRunning(false);
    setIsWarmingUp(false);
  }, []);

  const clearResults = useCallback(() => {
    stopPing();
    pingCountRef.current = 0;
    setPingCount(0);
    setLog([]);
    setServerData(
      Object.fromEntries(
        PING_SERVERS.map((s) => [
          s.id,
          { latestMs: null, history: Array(MAX_HISTORY).fill(null), min: null, max: null, avg: null, count: 0, errors: 0, total: 0 },
        ])
      )
    );
  }, [stopPing]);

  // Compute summary stats
  const allLatencies = Object.values(serverData)
    .map((d) => d.latestMs)
    .filter((v) => v !== null);
  const overallAvg =
    allLatencies.length > 0
      ? Math.round(allLatencies.reduce((a, b) => a + b, 0) / allLatencies.length)
      : null;
  const overallMin =
    allLatencies.length > 0 ? Math.min(...allLatencies) : null;
  const totalErrors = Object.values(serverData).reduce(
    (a, d) => a + d.errors,
    0
  );
  const totalPings = Object.values(serverData).reduce(
    (a, d) => a + d.count,
    0
  );
  const packetLoss =
    totalPings > 0 ? Math.round((totalErrors / totalPings) * 100) : 0;

  return (
    <div className="ping-test-page">
      <Helmet>
        <title>Server Ping Test – Live Latency Check | ClubHosty</title>
        <meta
          name="description"
          content="Test live server ping latency from your location to ClubHosty, Google, Cloudflare, AWS and more. Real-time network round-trip time measurement."
        />
        <meta
          name="keywords"
          content="ping test, server latency, network speed, live ping, round trip time, RTT, ClubHosty ping"
        />
      </Helmet>

      {/* Hero */}
      <section className="ping-hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-badge">
              <FaSignal /> Live Network Ping Test
            </div>
            <h1 className="hero-title">
              Test Server <strong>Latency</strong>
            </h1>
            <p className="hero-desc">
              Measure live round-trip ping times from your browser to servers
              around the world. Identify the fastest servers for your location.
            </p>
            {userLocation && (
              <div className="location-bar">
                <FaMapMarkerAlt />
                Pinging from: <strong>{userLocation}</strong>
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="container" style={{ paddingBottom: "60px" }}>
        {/* Info Banner */}
        <div className="info-banner">
          <FaInfoCircle className="info-banner-icon" />
          <p className="info-banner-text">
            <strong>How it works:</strong> Your browser sends HTTP requests to
            each server and measures the round-trip time. Results may be
            slightly higher than ICMP ping due to browser overhead, but
            accurately reflect real-world HTTP latency from your location.
            Ratings: <strong style={{ color: "#28a745" }}>Excellent</strong> (
            &lt;80ms) · <strong style={{ color: "#ffc107" }}>Fair</strong>{" "}
            (80–200ms) · <strong style={{ color: "#dc3545" }}>Poor</strong> (&gt;200ms).
          </p>
        </div>

        {/* Control Panel */}
        <div className="ping-control-panel">
          <div className="control-header">
            <div className="control-title">
              <FaServer /> Ping Configuration
            </div>
            <div className="control-actions">
              {!isRunning ? (
                <button
                  className="btn-ping-start"
                  onClick={startPing}
                  disabled={isRunning}
                >
                  <FaPlay /> Start Ping
                </button>
              ) : (
                <button className="btn-ping-stop" onClick={stopPing}>
                  <FaStop /> Stop
                </button>
              )}
              <button className="btn-ping-clear" onClick={clearResults}>
                <FaTrash /> Clear
              </button>
            </div>
          </div>
          <div className="ping-options">
            <span className="option-label">Ping Count:</span>
            {[10, 20, 50, 100].map((n) => (
              <button
                key={n}
                className={`count-btn ${selectedCount === n ? "active" : ""}`}
                onClick={() => setSelectedCount(n)}
                disabled={isRunning}
              >
                {n}
              </button>
            ))}
            {isRunning && (
              <span
                style={{
                  fontSize: "0.85rem",
                  color: "#21bcc3",
                  fontWeight: 600,
                  marginLeft: 8,
                }}
              >
                {isWarmingUp
                  ? "Warming up connections..."
                  : `Round ${pingCount}/${selectedCount}`}
              </span>
            )}
          </div>
        </div>

        {/* Summary */}
        {totalPings > 0 && (
          <div className="ping-summary">
            <div className="summary-stat">
              <div className="summary-stat-value">
                {overallAvg !== null ? `${overallAvg}` : "—"}
                <span style={{ fontSize: "1rem", fontWeight: 400 }}>ms</span>
              </div>
              <div className="summary-stat-label">Avg Latency</div>
            </div>
            <div className="summary-divider" />
            <div className="summary-stat">
              <div className="summary-stat-value">
                {overallMin !== null ? `${overallMin}` : "—"}
                <span style={{ fontSize: "1rem", fontWeight: 400 }}>ms</span>
              </div>
              <div className="summary-stat-label">Best Latency</div>
            </div>
            <div className="summary-divider" />
            <div className="summary-stat">
              <div className="summary-stat-value">{pingCount}</div>
              <div className="summary-stat-label">Rounds Completed</div>
            </div>
            <div className="summary-divider" />
            <div className="summary-stat">
              <div className="summary-stat-value">{packetLoss}%</div>
              <div className="summary-stat-label">Packet Loss</div>
            </div>
          </div>
        )}

        {/* Server Cards */}
        <div className="servers-grid">
          {PING_SERVERS.map((server) => (
            <ServerCard
              key={server.id}
              server={server}
              pingData={serverData[server.id]}
              isPinging={isRunning}
            />
          ))}
        </div>

        {/* Log */}
        <div className="ping-log-section">
          <div className="log-header">
            <span className="log-title">
              <FaSignal /> Ping Log
            </span>
          </div>
          <div className="log-container" ref={logContainerRef}>
            {log.length === 0 ? (
              <div className="log-empty">No results yet. Press Start Ping to begin.</div>
            ) : (
              log.map((entry, i) => (
                <div key={i} className="log-line">
                  <span className="log-time">[{entry.time}]</span>
                  <span className="log-target">{entry.target}</span>
                  <span className={`log-result ${entry.ms === null ? "timeout" : entry.quality}`}>
                    {entry.ms === null
                      ? "Request timeout"
                      : `Reply: time=${entry.ms}ms`}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PingTest;
